import {
  classifyCombination,
  enumerateLegalPlays,
  getCard,
  NATURAL_RANKS,
  naturalRankValue,
  type CardId,
  type CombinationKind,
  type NaturalRank,
  type NormalizedCombination,
} from "@guandan/game-core";

export type Suit = "♠" | "♥" | "♣" | "♦" | "🃏";

export interface CardView {
  readonly id: string;
  readonly rank: string;
  readonly suit: Suit;
  readonly red: boolean;
  readonly rankValue: number;
  readonly originalIndex: number;
}

export function cardFromId(id: string, originalIndex = 0): CardView {
  const card = getCard(id as CardId);
  if (card.rank === "SJ" || card.rank === "BJ") {
    return {
      id,
      rank: card.rank === "SJ" ? "小王" : "大王",
      suit: "🃏",
      red: card.rank === "BJ",
      rankValue: card.rank === "SJ" ? 16 : 17,
      originalIndex,
    };
  }
  const suit = (
    {
      spades: "♠",
      hearts: "♥",
      clubs: "♣",
      diamonds: "♦",
      joker: "🃏",
    } as const
  )[card.suit];
  return {
    id,
    rank: card.rank,
    suit,
    red: card.suit === "hearts" || card.suit === "diamonds",
    rankValue: naturalRankValue(card.rank),
    originalIndex,
  };
}

export function normalizedCombinationFromKey(
  cardIds: readonly string[],
  normalizationKey: string,
): NormalizedCombination | null {
  const [
    kindValue,
    bombSizeValue,
    mainValueValue,
    mainRankValue,
    assignmentsValue = "",
  ] = normalizationKey.split("|");
  const kinds: readonly CombinationKind[] = [
    "single",
    "pair",
    "triple",
    "full-house",
    "straight",
    "consecutive-pairs",
    "steel-plate",
    "bomb",
    "straight-flush",
    "joker-bomb",
  ];
  if (!kinds.includes(kindValue as CombinationKind)) return null;
  const kind = kindValue as CombinationKind;
  const bombSize = Number(bombSizeValue);
  const mainValue = Number(mainValueValue);
  const mainRank =
    mainRankValue === "-" ? null : (mainRankValue as NaturalRank);
  if (!Number.isFinite(bombSize) || !Number.isFinite(mainValue)) return null;
  const wildcardAssignments =
    assignmentsValue.length === 0
      ? []
      : assignmentsValue.split(",").flatMap((value) => {
          const separator = value.lastIndexOf(":");
          if (separator < 1) return [];
          return [
            {
              cardId: value.slice(0, separator) as CardId,
              asRank: value.slice(separator + 1) as NaturalRank,
            },
          ];
        });
  return {
    kind,
    cardIds: cardIds as readonly CardId[],
    mainRank,
    mainValue,
    cardCount: cardIds.length,
    bombSize,
    isBomb:
      kind === "bomb" || kind === "straight-flush" || kind === "joker-bomb",
    wildcardAssignments,
    normalizationKey,
  };
}

/** Orders every publicly played physical card by its resolved in-combination
 * rank. Wild level cards use their authoritative assignment, so sequences,
 * consecutive pairs and steel plates read naturally from low to high. */
export function sortPlayedCardIds(
  cardIds: readonly string[],
  combinationValue: NormalizedCombination | string,
): readonly string[] {
  const combination =
    typeof combinationValue === "string"
      ? normalizedCombinationFromKey(cardIds, combinationValue)
      : combinationValue;
  if (!combination)
    return [...cardIds].sort((left, right) =>
      left.localeCompare(right, undefined, { numeric: true }),
    );
  const assignments = new Map(
    combination.wildcardAssignments.map((assignment) => [
      assignment.cardId,
      assignment.asRank,
    ]),
  );
  const wheel =
    (combination.kind === "straight" ||
      combination.kind === "straight-flush") &&
    combination.mainRank === "5";
  const resolvedValue = (cardId: string) => {
    const assignedRank = assignments.get(cardId as CardId);
    if (assignedRank)
      return assignedRank === "A" && wheel ? 1 : naturalRankValue(assignedRank);
    const card = getCard(cardId as CardId);
    if (card.rank === "SJ") return 16;
    if (card.rank === "BJ") return 17;
    return card.rank === "A" && wheel ? 1 : naturalRankValue(card.rank);
  };
  return [...cardIds].sort((left, right) => {
    const value = resolvedValue(left) - resolvedValue(right);
    if (value !== 0) return value;
    return left.localeCompare(right, undefined, { numeric: true });
  });
}

export type SortMode =
  "original" | "rank" | "suit" | "type" | "smart" | "manual";

export type RecognitionPriority = "bomb" | "straight-flush";

export type LockGroupKind =
  | "bomb"
  | "straight-flush"
  | "straight"
  | "consecutive-pairs"
  | "steel-plate"
  | "custom";

export interface LockedGroup {
  readonly id: string;
  readonly kind: LockGroupKind;
  readonly label: string;
  readonly cardIds: readonly string[];
  readonly legalCombination: boolean;
  readonly explanation: string;
}

export interface LockCandidateDescription {
  readonly kind: LockGroupKind;
  readonly label: string;
  readonly legalCombination: boolean;
  readonly sourceKind: CombinationKind | null;
  readonly explanation: string;
}

export interface SmartCardGroup {
  readonly kind: CombinationKind;
  readonly label: string;
  readonly cardIds: readonly string[];
}

export interface SortCardsOptions {
  readonly levelRank?: string;
  readonly lockedGroups?: readonly LockedGroup[];
  readonly manualOrder?: readonly string[];
  readonly rankDirection?:
    "ascending-left-to-right" | "ascending-right-to-left";
}

export interface HandCardColumn {
  readonly rank: string;
  readonly cards: readonly CardView[];
}

/** Groups equal point values into one visual column while retaining the
 * selected ordering of rank columns and the deterministic suit order inside
 * each column. */
export function groupCardsByRank(
  cards: readonly CardView[],
): readonly HandCardColumn[] {
  const columns = new Map<string, CardView[]>();
  for (const card of cards) {
    const column = columns.get(card.rank);
    if (column) column.push(card);
    else columns.set(card.rank, [card]);
  }
  return [...columns].map(([rank, columnCards]) => ({
    rank,
    cards: columnCards,
  }));
}

const combinationLabels: Readonly<Record<CombinationKind, string>> = {
  single: "单张",
  pair: "对子",
  triple: "三张",
  "full-house": "三带二",
  straight: "顺子",
  "consecutive-pairs": "连对",
  "steel-plate": "钢板",
  bomb: "炸弹",
  "straight-flush": "同花顺",
  "joker-bomb": "四王炸",
};

const bombFirstPriority: Readonly<Record<CombinationKind, number>> = {
  "joker-bomb": 0,
  bomb: 1,
  "straight-flush": 2,
  "steel-plate": 3,
  "consecutive-pairs": 4,
  straight: 5,
  "full-house": 6,
  triple: 7,
  pair: 8,
  single: 9,
};

const straightFlushFirstPriority: Readonly<Record<CombinationKind, number>> = {
  "straight-flush": 0,
  "joker-bomb": 1,
  bomb: 2,
  "steel-plate": 3,
  "consecutive-pairs": 4,
  straight: 5,
  "full-house": 6,
  triple: 7,
  pair: 8,
  single: 9,
};

const smartDisplayPriority: Readonly<Record<CombinationKind, number>> = {
  "steel-plate": 0,
  "consecutive-pairs": 1,
  straight: 2,
  "full-house": 3,
  triple: 4,
  pair: 5,
  single: 6,
  "straight-flush": 7,
  bomb: 8,
  "joker-bomb": 9,
};

const straightFlushDisplayPriority: Readonly<Record<CombinationKind, number>> =
  {
    "steel-plate": 0,
    "consecutive-pairs": 1,
    straight: 2,
    "full-house": 3,
    triple: 4,
    pair: 5,
    single: 6,
    bomb: 7,
    "joker-bomb": 8,
    "straight-flush": 9,
  };

/**
 * Builds a deterministic, disjoint partition of a hand using the same legal
 * combination classifier as the authoritative game. Every physical card id is
 * emitted exactly once; repeated calls with the same hand have the same order.
 */
export function recognizeSmartGroups(
  cards: readonly CardView[],
  levelRankInput: string = "2",
  priority: RecognitionPriority = "bomb",
): readonly SmartCardGroup[] {
  const levelRank = normalizeLevelRank(levelRankInput);
  const kindPriority =
    priority === "straight-flush"
      ? straightFlushFirstPriority
      : bombFirstPriority;
  const remaining = new Map(cards.map((card) => [card.id, card]));
  const groups: SmartCardGroup[] = [];

  while (remaining.size > 1) {
    const legal = enumerateLegalPlays(
      [...remaining.keys()] as CardId[],
      null,
      { levelRank },
      { physicalVariants: false },
    )
      .filter((play) => play.cardIds.length > 1)
      .sort((left, right) => {
        const kind =
          kindPriority[left.combination.kind] -
          kindPriority[right.combination.kind];
        if (kind !== 0) return kind;
        const size = right.cardIds.length - left.cardIds.length;
        if (size !== 0) return size;
        const strength =
          left.combination.mainValue - right.combination.mainValue;
        if (strength !== 0) return strength;
        return cardIdKey(left.cardIds).localeCompare(
          cardIdKey(right.cardIds),
          undefined,
          { numeric: true },
        );
      });
    const chosen = legal[0];
    if (!chosen) break;
    const cardIds = [...chosen.cardIds];
    groups.push({
      kind: chosen.combination.kind,
      label: combinationLabels[chosen.combination.kind],
      cardIds,
    });
    for (const cardId of cardIds) remaining.delete(cardId);
  }

  const singles = [...remaining.values()].sort((left, right) =>
    compareRankCards(left, right, levelRank),
  );
  for (const card of singles) {
    groups.push({ kind: "single", label: "单张", cardIds: [card.id] });
  }
  return groups;
}

export function describeLockCandidate(
  cardIds: readonly string[],
  levelRankInput: string = "2",
): LockCandidateDescription {
  const levelRank = normalizeLevelRank(levelRankInput);
  const unique = [...new Set(cardIds)];
  if (unique.length !== cardIds.length || unique.length === 0) {
    return {
      kind: "custom",
      label: "自定义",
      legalCombination: false,
      sourceKind: null,
      explanation:
        unique.length === 0
          ? "尚未选择牌张，不能创建锁组。"
          : "所选牌包含重复实体牌，不能识别为合法牌型。",
    };
  }

  let combination: ReturnType<typeof classifyCombination> = null;
  try {
    combination = classifyCombination(unique as CardId[], { levelRank });
  } catch {
    combination = null;
  }
  if (!combination) {
    return {
      kind: "custom",
      label: "自定义",
      legalCombination: false,
      sourceKind: null,
      explanation:
        "所选牌不构成炸弹、同花顺、顺子、连对或钢板；仍可作为自定义锁组保持相邻。",
    };
  }

  const recognizedKind = lockKindForCombination(combination.kind);
  if (recognizedKind) {
    const label =
      combination.kind === "joker-bomb"
        ? "炸弹（四王炸）"
        : combinationLabels[combination.kind];
    return {
      kind: recognizedKind,
      label,
      legalCombination: true,
      sourceKind: combination.kind,
      explanation: `已按合法${label}锁为一组。`,
    };
  }

  const sourceLabel = combinationLabels[combination.kind];
  return {
    kind: "custom",
    label: `自定义（${sourceLabel}）`,
    legalCombination: true,
    sourceKind: combination.kind,
    explanation: `所选牌是合法${sourceLabel}，作为自定义锁组保持相邻。`,
  };
}

export function createLockedGroup(
  cardIds: readonly string[],
  levelRank: string = "2",
): LockedGroup {
  const unique = [...new Set(cardIds)];
  const description = describeLockCandidate(cardIds, levelRank);
  return {
    id: `lock-${cardIdKey(unique)}`,
    kind: description.kind,
    label: description.label,
    cardIds: unique,
    legalCombination: description.legalCombination,
    explanation: description.explanation,
  };
}

export function lockedCardIds(
  groups: readonly LockedGroup[],
): ReadonlySet<string> {
  return new Set(groups.flatMap((group) => group.cardIds));
}

export function reconcileLockedGroups(
  groups: readonly LockedGroup[],
  handCardIds: readonly string[],
): readonly LockedGroup[] {
  const hand = new Set(handCardIds);
  const seen = new Set<string>();
  return groups.filter((group) => {
    if (
      group.cardIds.length === 0 ||
      group.cardIds.some((cardId) => !hand.has(cardId) || seen.has(cardId))
    ) {
      return false;
    }
    for (const cardId of group.cardIds) seen.add(cardId);
    return true;
  });
}

export function sortCards(
  cards: readonly CardView[],
  mode: SortMode,
  options: SortCardsOptions = {},
): CardView[] {
  const levelRank = options.levelRank ?? "2";
  const byId = new Map(cards.map((card) => [card.id, card]));
  const activeGroups = reconcileLockedGroups(
    options.lockedGroups ?? [],
    cards.map((card) => card.id),
  );
  const lockedIds = lockedCardIds(activeGroups);
  const locked = activeGroups.flatMap((group) =>
    group.cardIds.flatMap((cardId) => {
      const card = byId.get(cardId);
      return card ? [card] : [];
    }),
  );
  const unlocked = cards.filter((card) => !lockedIds.has(card.id));

  let ordered: CardView[];
  if (mode === "original") {
    ordered = [...unlocked].sort(
      (left, right) => left.originalIndex - right.originalIndex,
    );
  } else if (mode === "suit") {
    ordered = [...unlocked].sort(
      (left, right) =>
        left.suit.localeCompare(right.suit) ||
        compareRankCards(left, right, levelRank),
    );
  } else if (mode === "type" || mode === "smart") {
    const recognized = recognizeSmartGroups(
      unlocked,
      levelRank,
      mode === "type" ? "straight-flush" : "bomb",
    );
    const displayPriority =
      mode === "type" ? straightFlushDisplayPriority : smartDisplayPriority;
    const groups = [...recognized].sort((left, right) => {
      const kind = displayPriority[left.kind] - displayPriority[right.kind];
      if (kind !== 0) return kind;
      return cardIdKey(left.cardIds).localeCompare(
        cardIdKey(right.cardIds),
        undefined,
        { numeric: true },
      );
    });
    ordered = groups.flatMap((group) =>
      group.cardIds.flatMap((cardId) => {
        const card = byId.get(cardId);
        return card ? [card] : [];
      }),
    );
  } else if (mode === "manual") {
    const positions = new Map(
      (options.manualOrder ?? []).map((cardId, index) => [cardId, index]),
    );
    ordered = [...unlocked].sort(
      (left, right) =>
        (positions.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
          (positions.get(right.id) ?? Number.MAX_SAFE_INTEGER) ||
        left.originalIndex - right.originalIndex,
    );
  } else {
    ordered = [...unlocked].sort((left, right) =>
      compareRankCards(left, right, levelRank),
    );
  }
  const combined = [...ordered, ...locked];
  return mode === "rank" && options.rankDirection === "ascending-right-to-left"
    ? combined.reverse()
    : combined;
}

function lockKindForCombination(
  kind: CombinationKind,
): Exclude<LockGroupKind, "custom"> | null {
  if (kind === "joker-bomb") return "bomb";
  if (
    kind === "bomb" ||
    kind === "straight-flush" ||
    kind === "straight" ||
    kind === "consecutive-pairs" ||
    kind === "steel-plate"
  ) {
    return kind;
  }
  return null;
}

function compareRankCards(
  left: CardView,
  right: CardView,
  levelRankInput: string,
): number {
  const levelRank = normalizeLevelRank(levelRankInput);
  const strength = (card: CardView) =>
    card.rank === levelRank ? 15 : card.rankValue;
  return (
    strength(left) - strength(right) ||
    left.suit.localeCompare(right.suit) ||
    left.id.localeCompare(right.id, undefined, { numeric: true })
  );
}

function cardIdKey(cardIds: readonly string[]): string {
  return [...cardIds]
    .sort((left, right) =>
      left.localeCompare(right, undefined, { numeric: true }),
    )
    .join("-");
}

function normalizeLevelRank(value: string): NaturalRank {
  return (NATURAL_RANKS as readonly string[]).includes(value)
    ? (value as NaturalRank)
    : "2";
}
